import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, Form, Button } from "react-bootstrap";
import { useGlobalState } from "../../services/hostelConst";
import {
  addBooking,
  getBooking,
  updateBooking,
} from "../../services/OrganisationServices/transportManagemetService";
import { callToast, showErrorMessages } from "../../services/utilityFunctions";

const AddBookingForm = ({ navigate, refresh }) => {
  const { closeModal, masterTableState, updateMasterTable, modalState } =
    useGlobalState();
  const id = modalState.id;

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(modalState.isEditing);

  const prepareFormData = (data = null) => {
    return {
      userId: data == null ? "" : data.USER_ID || data._USER || "",
      routeId: data == null ? "" : data.ROUTE_ID || data._ROUTE || "",
      bookingDate: data == null ? "" : data.BOOKING_DATE || "",
      journeyDate: data == null ? "" : data.JOURNEY_DATE || "",
      seatNumber: data == null ? "" : data.SEAT_NUMBER || "",
      status: data == null ? "PENDING" : data.STATUS || "PENDING",
      pickupLocation: data == null ? "" : data.PICKUP_LOCATION || "",
      dropLocation: data == null ? "" : data.DROP_LOCATION || "",
      fare: data == null ? "" : data.FARE || "",
    };
  };

  const [formData, setFormData] = useState(prepareFormData());

  const getBookingDetailsData = async (id) => {
    setLoadingData(true);
    try {
      const res = await getBooking(id);
      if (res.status === "success") {
        setFormData(prepareFormData(res.data));
      } else {
        showErrorMessages({ data: res, navigate });
        closeModal();
      }
    } catch (error) {
      console.error("Error fetching booking details:", error);
      showErrorMessages({
        data: { message: "Failed to load booking details" },
        navigate,
      });
      closeModal();
    }
    setLoadingData(false);
  };

  useEffect(() => {
    if (modalState.isEditing && id) {
      getBookingDetailsData(id);
    }
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.userId.trim()) {
      newErrors.userId = "User ID is required";
    }
    if (!formData.routeId.trim()) {
      newErrors.routeId = "Route ID is required";
    }
    if (!formData.bookingDate) {
      newErrors.bookingDate = "Booking date is required";
    }
    if (!formData.journeyDate) {
      newErrors.journeyDate = "Journey date is required";
    }
    if (!formData.seatNumber.trim()) {
      newErrors.seatNumber = "Seat number is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        userId: formData.userId,
        routeId: formData.routeId,
        bookingDate: formData.bookingDate,
        journeyDate: formData.journeyDate,
        seatNumber: formData.seatNumber,
        status: formData.status,
        pickupLocation: formData.pickupLocation,
        dropLocation: formData.dropLocation,
        fare: formData.fare ? parseFloat(formData.fare) : null,
      };

      let res;
      if (modalState.isEditing) {
        submitData.entityId = id;
        res = await updateBooking(submitData);
      } else {
        res = await addBooking(submitData);
      }

      if (res.status === "success") {
        callToast(
          "success",
          "Success",
          `Booking ${modalState.isEditing ? "updated" : "added"} successfully`
        );
        updateMasterTable(true);
        closeModal();
      } else {
        showErrorMessages({ data: res, navigate });
      }
    } catch (error) {
      console.error("Error submitting booking:", error);
    }
    setLoading(false);
  };

  if (loadingData) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <h5 className="card-title">
          {modalState.isEditing ? "Edit" : "Add"} Booking
        </h5>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>User ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.userId}
                  onChange={(e) => handleChange("userId", e.target.value)}
                  isInvalid={!!errors.userId}
                  placeholder="Enter user ID"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.userId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Route ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.routeId}
                  onChange={(e) => handleChange("routeId", e.target.value)}
                  isInvalid={!!errors.routeId}
                  placeholder="Enter route ID"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.routeId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Booking Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.bookingDate}
                  onChange={(e) => handleChange("bookingDate", e.target.value)}
                  isInvalid={!!errors.bookingDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.bookingDate}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Journey Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.journeyDate}
                  onChange={(e) => handleChange("journeyDate", e.target.value)}
                  isInvalid={!!errors.journeyDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.journeyDate}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Seat Number *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.seatNumber}
                  onChange={(e) => handleChange("seatNumber", e.target.value)}
                  isInvalid={!!errors.seatNumber}
                  placeholder="Enter seat number"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.seatNumber}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Pickup Location</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.pickupLocation}
                  onChange={(e) =>
                    handleChange("pickupLocation", e.target.value)
                  }
                  placeholder="Enter pickup location"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Drop Location</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.dropLocation}
                  onChange={(e) => handleChange("dropLocation", e.target.value)}
                  placeholder="Enter drop location"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Fare</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={formData.fare}
                  onChange={(e) => handleChange("fare", e.target.value)}
                  placeholder="Enter fare amount"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                >
                  <option value="PENDING">Pending</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="NO_SHOW">No Show</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {modalState.isEditing ? "Updating..." : "Adding..."}
                </>
              ) : modalState.isEditing ? (
                "Update Booking"
              ) : (
                "Add Booking"
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AddBookingForm;
